import { useState, useEffect } from 'react'
import { CartState } from '@store/redux/reducers/cartReducer'
import trackError from '@helpers/trackError'
import { analytics } from '@helpers/analytics'
import { getRecommendations } from '@api/recommendations'
import Translations from '@helpers/translations'
import { productListViewed } from '@helpers/products'
import { getProductSkuProductId } from '@helpers/products'
import { RecommendedProduct } from 'types/recommendations'

const SUGGESTED_PRODUCTS_SUPPORTED_STORES = ['GB', 'US']

const MULTIBUY_DISCOUNTS_DATA = {
  'original t-shirt': {
    label: Translations.localise('common.cart.multibuy.t-shirts.label'),
    discount: Translations.localise('common.cart.multibuy.t-shirts.discount'),
    eligiblePrice: Translations.localise(
      'common.cart.multibuy.t-shirts.eligiblePrice'
    )
  },
  trunks: {
    label: Translations.localise('common.cart.multibuy.trunks.label'),
    discount: Translations.localise('common.cart.multibuy.trunks.discount'),
    eligiblePrice: Translations.localise(
      'common.cart.multibuy.trunks.eligiblePrice'
    )
  },
  belts: {
    label: Translations.localise('common.cart.multibuy.belts.label'),
    discount: '',
    eligiblePrice: Translations.localise(
      'common.cart.multibuy.belts.eligiblePrice'
    ),
    discountedPrice: '69.00',
    title: 'Add the matching belt in your size'
  },
  'heavyweight tee': {
    label: Translations.localise('common.cart.multibuy.heavyweight-tee.label'),
    discount: Translations.localise(
      'common.cart.multibuy.heavyweight-tee.discount'
    ),
    eligiblePrice: Translations.localise(
      'common.cart.multibuy.heavyweight-tee.eligiblePrice'
    )
  }
} as const

export const RECOMMENDATIONS_OVERRIDES = {
  tops: {
    'The Torino Suit - Jacket': [
      { productType: 'Sharp Shirt', productCategory: 'tops' }
    ],
    'The Linen Suit - Jacket': [
      { productType: 'Oxford Shirt', productCategory: 'tops' }
    ]
  },
  trousers: {
    'The Torino Suit - Trouser': [
      { productType: 'Sharp Shirt', productCategory: 'tops' }
    ],
    'The Linen Suit - Trouser': [
      { productType: 'Oxford Shirt', productCategory: 'tops' }
    ]
  }
}

function sendProductListViewedEvent(products: RecommendedProduct[]) {
  const eventProducts = products.map((product, index) => ({
    quantity: 1,
    name: product.product_type,
    color: product.title,
    price: parseFloat(product.price),
    position: index + 1,
    url: `/products/${product.handle}`,
    image_url: product.image,
    brand: 'SPOKE',
    product_id: getProductSkuProductId({ sku: product.sku })
  }))
  productListViewed({
    context: 'Add On',
    products: eventProducts
  })
}

const getMultibuyDiscount = (productType = '', price = '') => {
  const lowerCaseProductType = productType.toLowerCase()
  const multibuyDiscountKey = (
    /belts/.test(lowerCaseProductType) ? 'belts' : lowerCaseProductType
  ) as keyof typeof MULTIBUY_DISCOUNTS_DATA
  const multibuyDiscountForProductType =
    MULTIBUY_DISCOUNTS_DATA[multibuyDiscountKey]
  if (
    !multibuyDiscountForProductType ||
    parseFloat(multibuyDiscountForProductType?.eligiblePrice) !==
      parseFloat(price)
  ) {
    return undefined
  }

  return multibuyDiscountForProductType
}

function useSuggestedProducts(cart: CartState) {
  const [fetchState, setFetchState] = useState('fetched')
  const [products, setProducts] = useState<RecommendedProduct[] | []>([])

  const firstRecommendedProduct = products[0]
  const multibuyDiscount = getMultibuyDiscount(
    firstRecommendedProduct?.product_type,
    firstRecommendedProduct?.price
  )

  useEffect(() => {
    const getSuggestedProducts = async () => {
      try {
        const suggestedProducts = await getRecommendations({
          cart,
          overrides: RECOMMENDATIONS_OVERRIDES
        })

        if (suggestedProducts) {
          setProducts(suggestedProducts)
          if (suggestedProducts.length) {
            sendProductListViewedEvent(suggestedProducts)
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Not error instance'
        trackError(new Error(`getSuggestedProducts error - ${errorMessage}`), {
          error: error as Error,
          cart
        })
      } finally {
        setFetchState('fetched')
      }
    }

    if (
      !firstRecommendedProduct &&
      SUGGESTED_PRODUCTS_SUPPORTED_STORES.includes(process.env.SHOP_LOCALE)
    ) {
      setFetchState('loading')
      getSuggestedProducts()
    }
  }, [firstRecommendedProduct])

  useEffect(() => {
    const { label }: { label?: string } = multibuyDiscount || {}
    if (label) {
      analytics('Addon Multibuy Promo Viewed', {
        multibuy: !!label
      })
    }
  }, [multibuyDiscount])

  if (!products) {
    return {}
  }

  return {
    suggestedProducts: products,
    multibuyDiscount,
    fetchState
  }
}

export default useSuggestedProducts
